import React, { useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * SEO Keyword Research — single-purpose tool.
 * `embedded` hides the top PageHeader so it can render inside MarketingPage's tab.
 */
export default function SeoPage({ embedded = false }) {
  const [kwTopic, setKwTopic] = useState("");
  const [kwLocation, setKwLocation] = useState("");
  const [kwData, setKwData] = useState(null);
  const [kwLoading, setKwLoading] = useState(false);

  const runKeywords = async () => {
    if (!kwTopic) return toast.error("Enter a topic");
    setKwLoading(true);
    try {
      const r = await api.post("/seo/keywords", { topic: kwTopic, location: kwLocation });
      setKwData(r.data);
    } catch {
      toast.error("Failed to generate keywords");
    } finally { setKwLoading(false); }
  };

  return (
    <div data-testid="seo-page">
      {!embedded && (
        <PageHeader
          eyebrow="SEO"
          title="Rank for the searches that pay your bills."
          description="Discover the keywords your customers are actually typing into Google."
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="sharp-card p-6">
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Topic / service</Label>
              <Input data-testid="kw-topic" placeholder="e.g. drywall repair" value={kwTopic} onChange={(e) => setKwTopic(e.target.value)} className="rounded-sm border-slate-300 mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Location (optional)</Label>
              <Input data-testid="kw-location" placeholder="e.g. Albury, NSW" value={kwLocation} onChange={(e) => setKwLocation(e.target.value)} className="rounded-sm border-slate-300 mt-1" />
            </div>
            <Button data-testid="btn-run-keywords" onClick={runKeywords} disabled={kwLoading} className="w-full bg-navy hover:bg-navy-deep text-white font-semibold rounded-sm h-11">
              <MagnifyingGlass className="mr-2" size={18} />{kwLoading ? "Researching..." : "Research keywords"}
            </Button>
          </div>
        </Card>
        <Card className="sharp-card p-6 lg:col-span-2 min-h-[300px]">
          {!kwData ? (
            <div className="text-slate-400 text-sm h-full flex items-center justify-center">Results will appear here.</div>
          ) : (
            <div className="space-y-5">
              <div>
                <div className="eyebrow mb-2">Primary keywords</div>
                <div className="space-y-2">
                  {(kwData.primary_keywords || []).map((k) => (
                    <div key={k.keyword} className="flex items-center justify-between p-3 border border-slate-200 rounded-sm">
                      <div>
                        <div className="font-semibold text-slate-900">{k.keyword}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{k.intent} · vol {k.monthly_volume}</div>
                      </div>
                      <Badge className="rounded-sm bg-slate-100 text-slate-700 border border-slate-200">{k.difficulty}</Badge>
                    </div>
                  ))}
                </div>
              </div>
              {kwData.long_tail && (
                <div>
                  <div className="eyebrow mb-2">Long-tail</div>
                  <div className="flex flex-wrap gap-2">
                    {kwData.long_tail.map((k) => <Badge key={k} variant="outline" className="rounded-sm">{k}</Badge>)}
                  </div>
                </div>
              )}
              {kwData.questions_people_ask && (
                <div>
                  <div className="eyebrow mb-2">Questions to answer</div>
                  <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                    {kwData.questions_people_ask.map((q) => <li key={q}>{q}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
